import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import { VariableMenu } from '@/components/admin/variables/variable-menu';
import {
  CONVERSATION_BOX_HELP,
  CONVERSATION_BOX_LABEL,
  CONVERSATION_CONTEXT_HELP,
  CONVERSATION_CONTEXT_LABEL,
  CONVERSATION_NO_DURATION,
  CONVERSATION_NO_TIMING,
  CONVERSATION_TRANSITION_OUT,
  conversationReasonSentence,
} from '@/lib/experience-labels';
import { cn } from '@/lib/utils';
import type { PaletteContent } from '@/lib/variable-palette';
import { firstPaletteToken, paletteFullSentence } from '@/lib/variable-palette';
import type { VariableZone } from '@/lib/variable-pill';
import type {
  AnimationSpeed,
  ConversationEligibility,
  ExperiencePageDraft,
  PageTransitionAnimation,
} from '@matter_extractor/shared';
import {
  EXPERIENCE_AUTO_DURATION_MAX,
  EXPERIENCE_AUTO_DURATION_MIN,
  EXPERIENCE_CONTEXT_MAX,
  EXPERIENCE_PAGE_NAME_MAX,
  EXPERIENCE_THANKS_MAX,
  feedbackThanksOf,
} from '@matter_extractor/shared';
import { Checkbox, Icon, TextArea, TextField, Tooltip } from '@pms-digital/design-system';
import { useId } from 'react';

/**
 * Le panneau « Page », en tête de la pile quand aucun bloc n'est sélectionné
 * (refonte modulaire §9.2, §9.3; planche `EditeurPageEcran`; issue #58).
 *
 * « Le nom, la durée automatique, et la transition vers la suivante en deux
 * gestes, l'animation par trois icônes (aucune, fondu, glissé) et la rapidité
 * par une bascule à trois segments. »
 *
 * ## La durée automatique est un champ VIDABLE, et le vide est « aucune »
 *
 * §9.2 makes it optional - « une durée facultative qui avance seule, pour un
 * intertitre » - and the schema stores `null`. A number input cleared to the
 * empty string is therefore `null` and not zero: a page set to advance after
 * 0 second is a page nobody can read, and it is also outside the bounds of H23
 * (1 to 600 s), so it would be a version the server refuses.
 *
 * ## La transition de la DERNIÈRE page est réglable quand même
 *
 * It is never played - there is no next page - and the banner says so by
 * omitting it. But the page can move: an operator who inserts a page after it
 * would otherwise find a transition they never chose. Nothing is greyed here,
 * and the sentence under the control says when it applies.
 *
 * ## La durée automatique se RETIRE sur la page protocole (§9.5, issue #58)
 *
 * The opposite arbitrage of the one just above, for the opposite reason. A
 * protocol page ends when the conversation does - « à la fin du protocole ...
 * un bouton "Continuer" passe à la page suivante » - so nothing there plays a
 * delay; `experiencePageSchema` accepts the field on any page, which makes a
 * live control a promise the session does not keep. The name and the transition
 * stay: both are read on the protocol page like on any other.
 *
 * ## La page de retour : la phrase de remerciement, et toujours pas de durée
 *
 * The feedback page (retour-experience.md §2, §7.2, issue #176) is a content
 * page for everything but two things, both of which this panel says. It has NO
 * automatic duration - « une page qui s'en va seule ne laisserait pas le temps
 * de répondre », and `feedback-page-exit` refuses one - so the field is gone, as
 * on the protocol page. And it has its PHRASE OF THANKS, between the name and the
 * transition: the text that replaces the blocks once the feedback is sent, plain
 * text of 600 characters, the counter drawn under it, and the one hint that says
 * what an empty phrase costs - the refusal of a version.
 *
 * ## « Jouer dans la conversation » (prolongation.md §3, §6 ; issue #186)
 *
 * Sous le nom, une case, avec sa phrase d'aide. Cochée, elle ouvre le « Contexte
 * servi à l'interviewer » - le champ et la portée de celui de la page protocole,
 * pour cette page-ci - et la durée automatique et la transition DISPARAISSENT :
 * « Pas de durée automatique ni de transition : la page avance au clic, dans le
 * fil ». Une page du fil qui garderait une durée serait refusée à l'enregistrement
 * (`conversation-page-auto-duration`), donc cocher la RETIRE - un champ caché qui
 * porterait encore une valeur serait un refus que rien à l'écran n'explique.
 *
 * Grisée, avec sa raison, sur une page qui ne peut pas la prendre
 * (`conversationEligibilityOf`, une phrase par raison). **Une page cochée qui a
 * cessé de pouvoir l'être** - une page ordinaire glissée avant elle - garde sa case
 * cochée ET VIVANTE : c'est la seule porte pour la décocher, et l'enregistrement
 * la refuse en la nommant. Le texte du contexte d'une page décochée n'est pas ici :
 * le corps de l'éditeur le garde pour qu'un re-cochage le retrouve (§3.2).
 *
 * Le panneau ne reçoit la case que si le corps la lui passe (`conversation`) : un
 * panneau monté seul - les tests d'avant, un écran qui n'en parle pas - ne la
 * dessine pas.
 *
 * The phrase is shown as the page READS it (`feedbackThanksOf`): a feedback page
 * stored without one reads as the template's, and the field would otherwise
 * open empty on a page that does say something to the person. The transition
 * stays settable and says it has no effect - nothing follows the page.
 */

const TRANSITIONS: readonly {
  value: PageTransitionAnimation;
  label: string;
  icon: string;
}[] = [
  { value: 'none', label: 'Aucune', icon: 'block' },
  { value: 'fade', label: 'Fondu', icon: 'contrast' },
  { value: 'slide', label: 'Glissé', icon: 'arrow_forward' },
];

const SPEEDS: readonly { value: AnimationSpeed; label: string }[] = [
  { value: 'fast', label: 'Rapide' },
  { value: 'normal', label: 'Normal' },
  { value: 'slow', label: 'Lent' },
];

export interface PagePanelProps {
  readonly page: ExperiencePageDraft;
  readonly readOnly: boolean;
  readonly isLast: boolean;
  /** False on the protocol page and on the feedback page, where nothing plays a delay - see the head. */
  readonly showDuration?: boolean;
  readonly onChange: (next: ExperiencePageDraft) => void;
  /** The box « Jouer dans la conversation » and its context - absent, the panel has neither. */
  readonly conversation?: PageConversationProps | undefined;
}

/** What the box and the context of a page played in the conversation need (§3.1, §3.2). */
export interface PageConversationProps {
  /** Whether the page may be ticked, and why not - `conversationEligibilityOf`. */
  readonly eligibility: ConversationEligibility;
  /** Ticked or not: the body writes `conversation`, and keeps the text of the context aside. */
  readonly onToggle: (ticked: boolean) => void;
  readonly onContext: (context: string) => void;
  /** What each token of the context is HERE - the zone of THIS page (§3.2). */
  readonly zone: VariableZone;
  /** The `@` menu at THIS page. */
  readonly palette: (query: string) => PaletteContent;
  /**
   * The page is the LAST of the thread and a page follows it: its transition is the one
   * that plays from the thread to that page (§3.3), so the panel keeps the setting.
   */
  readonly keepsTransition?: boolean;
}

export function PagePanel({
  page,
  readOnly,
  isLast,
  showDuration = true,
  onChange,
  conversation,
}: PagePanelProps) {
  const contextLabelId = useId();
  const feedback = page.kind === 'feedback';
  const thanks = feedback ? feedbackThanksOf(page) : '';
  const ticked = page.conversation !== null;
  /* Ticked pages lose the transition, but the last of the thread: it leaves the thread by it. */
  const transitionOut = ticked && conversation?.keepsTransition !== true;
  const blocked =
    conversation !== undefined && !conversation.eligibility.allowed
      ? conversation.eligibility
      : null;
  /* A page that is NOT ticked and cannot be: the box is grey. A ticked one stays live to be unticked. */
  const boxClosed = readOnly || (blocked !== null && !ticked);

  return (
    <section
      data-testid={PAGE_EDITOR_IDS.pagePanel}
      className="space-y-3 rounded-md border-2 border-gray-200 bg-white p-3"
    >
      <h2 className="font-semibold text-bleu-nuit text-small">Page</h2>

      <TextField
        label="Nom de la page"
        compact
        data-testid={PAGE_EDITOR_IDS.pageName}
        value={page.name}
        readOnly={readOnly}
        maxLength={EXPERIENCE_PAGE_NAME_MAX}
        onChange={(event) => onChange({ ...page, name: event.currentTarget.value })}
      />

      {feedback ? (
        <div className="space-y-1.5">
          {/* An empty phrase is refused at the save: `status="error"` is what sets
              `aria-invalid` on the package field, and borders it in red. */}
          <TextArea
            label="Phrase de remerciement"
            data-testid={PAGE_EDITOR_IDS.pageThanks}
            value={thanks}
            readOnly={readOnly}
            maxLength={EXPERIENCE_THANKS_MAX}
            rows={3}
            status={thanks.trim() === '' ? 'error' : 'default'}
            onChange={(event) => onChange({ ...page, thanks: event.currentTarget.value })}
          />
          <p
            data-testid={PAGE_EDITOR_IDS.pageThanksCount}
            className="text-right text-gray-600 text-caption tabular-nums"
          >
            {thanks.length} / {EXPERIENCE_THANKS_MAX}
          </p>
          <p className="text-gray-600 text-caption leading-4">
            Ce qui remplace les blocs de la page quand la personne a envoyé son retour. Vide,
            l’enregistrement d’une version est refusé.
          </p>
        </div>
      ) : null}

      {conversation === undefined ? null : (
        <div className="space-y-2">
          <Checkbox
            label={CONVERSATION_BOX_LABEL}
            description={
              <span data-testid={blocked === null ? undefined : PAGE_EDITOR_IDS.conversationReason}>
                {blocked === null
                  ? CONVERSATION_BOX_HELP
                  : conversationReasonSentence(blocked.reason)}
              </span>
            }
            data-testid={PAGE_EDITOR_IDS.conversationBox}
            checked={ticked}
            disabled={boxClosed}
            onCheckedChange={conversation.onToggle}
          />

          {ticked ? (
            <div className="space-y-1.5">
              <p id={contextLabelId} className="font-semibold text-gray-800 text-caption">
                {CONVERSATION_CONTEXT_LABEL}
              </p>
              <PaintedTextField
                value={page.conversation?.context ?? ''}
                readOnly={readOnly}
                placeholder="Ce que l’interviewer doit savoir pour amener cette page : @prénom, @noyau…"
                ariaLabelledBy={contextLabelId}
                testId={PAGE_EDITOR_IDS.conversationContext}
                insertTestId={PAGE_EDITOR_IDS.conversationContextInsert}
                minHeightClass="min-h-24"
                zone={conversation.zone}
                renderMenu={(query, insert, fits) => (
                  <VariableMenu
                    content={conversation.palette(query ?? '')}
                    query={query}
                    onInsert={insert}
                    fits={fits}
                    full={paletteFullSentence(EXPERIENCE_CONTEXT_MAX)}
                    className="mt-2"
                  />
                )}
                firstToken={(query) => firstPaletteToken(conversation.palette(query))}
                // The counter is the bound, refused and never cut (#160).
                counter={{
                  max: EXPERIENCE_CONTEXT_MAX,
                  testId: PAGE_EDITOR_IDS.conversationContextCount,
                }}
                onChange={conversation.onContext}
              />
              <p className="text-gray-600 text-caption leading-4">{CONVERSATION_CONTEXT_HELP}</p>
            </div>
          ) : null}
        </div>
      )}

      {!showDuration || ticked ? null : (
        <TextField
          label="Durée automatique"
          compact
          help="secondes, vide pour aucune"
          type="number"
          inputMode="numeric"
          data-testid={PAGE_EDITOR_IDS.pageDuration}
          value={page.autoDurationSeconds ?? ''}
          readOnly={readOnly}
          min={EXPERIENCE_AUTO_DURATION_MIN}
          max={EXPERIENCE_AUTO_DURATION_MAX}
          placeholder="aucune"
          onChange={(event) => {
            const raw = event.currentTarget.value.trim();
            // Vidé, c'est « aucune » et jamais zéro - voir la tête du fichier.
            const seconds = raw === '' ? null : Number(raw);
            onChange({
              ...page,
              autoDurationSeconds:
                seconds === null || Number.isNaN(seconds)
                  ? null
                  : /*
                        Borné aux 1 à 600 s de H23, à la frappe : hors de ces
                        bornes le contenu est un contenu que le schéma refuse,
                        et le refus serait un 409 que rien à l'écran n'explique.
                      */
                    Math.min(
                      Math.max(Math.round(seconds), EXPERIENCE_AUTO_DURATION_MIN),
                      EXPERIENCE_AUTO_DURATION_MAX,
                    ),
            });
          }}
        />
      )}

      {ticked ? (
        <p
          data-testid={PAGE_EDITOR_IDS.conversationNoTiming}
          className="text-gray-600 text-caption leading-4"
        >
          {transitionOut ? CONVERSATION_NO_TIMING : CONVERSATION_NO_DURATION}
        </p>
      ) : null}

      {transitionOut ? null : (
        <div className="space-y-1.5">
          <p className="font-semibold text-gray-800 text-caption">Transition vers la suivante</p>
          <div className="flex flex-wrap items-center gap-2">
            <div role="radiogroup" aria-label="Animation de la transition" className="flex gap-1">
              {TRANSITIONS.map(({ value, label, icon }) => {
                const chosen = page.transition.animation === value;
                return (
                  <Tooltip key={value} content={label}>
                    <button
                      type="button"
                      // biome-ignore lint/a11y/useSemanticElements: three icon buttons, not three inputs - the icon IS the label.
                      role="radio"
                      aria-checked={chosen}
                      aria-label={label}
                      disabled={readOnly}
                      onClick={() =>
                        onChange({ ...page, transition: { ...page.transition, animation: value } })
                      }
                      className={cn(
                        'inline-flex size-8 cursor-pointer items-center justify-center rounded-md border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:opacity-60',
                        chosen
                          ? 'border-bleu-nuit bg-bleu-nuit text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400',
                      )}
                    >
                      <Icon name={icon} size={16} />
                    </button>
                  </Tooltip>
                );
              })}
            </div>
            <Segmented
              label="Rapidité de la transition"
              size="sm"
              value={page.transition.speed}
              options={SPEEDS}
              readOnly={readOnly}
              onChange={(speed) => onChange({ ...page, transition: { ...page.transition, speed } })}
            />
          </div>
          {feedback ? (
            <p className="text-gray-600 text-caption leading-4">
              Sans effet : la page de retour est la dernière. Pas de durée automatique ici, la page
              ne se quitte pas, elle s’envoie.
            </p>
          ) : ticked ? (
            <p
              data-testid={PAGE_EDITOR_IDS.conversationTransitionOut}
              className="text-gray-600 text-caption leading-4"
            >
              {CONVERSATION_TRANSITION_OUT}
            </p>
          ) : isLast ? (
            <p className="text-gray-600 text-caption leading-4">
              Dernière page de la ligne : cette transition ne se joue pas tant qu’aucune page ne la
              suit.
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
